import { useState } from 'react';
import {
  EPOXY_PRIMERS,
  FLOOR_PREP_PRODUCTS,
  HIGH_BUILD_EPOXY,
  FLAKE_PRODUCTS,
  POLYASPARTIC_SEAL,
  HIGH_BUILD_SYSTEMS
} from '@/components/proepoxy/productData';
import { calculateOptimalPacks } from '@/components/proepoxy/utils';

const useHighBuildOrder = () => {
  const [highBuildAreas, setHighBuildAreas] = useState([]);
  const [showInstallationGuide, setShowInstallationGuide] = useState(false);
  const [showColourChart, setShowColourChart] = useState(false);

  // Manual order state
  const [manualOrder, setManualOrder] = useState({
    primerItems: [],
    highBuildItems: [],
    flakeItems: [],
    polyaspartic5m2: 0,
    polyaspartic20m2: 0,
    needsSmoothingCompounds: null,
    smoothingCompounds: {},
    needsSubFloorPrimer: null,
    subFloorPrimers: {}
  });

  // Manual order line item helpers
  const addPrimerItem = () => {
    const newItem = { id: `primer_${Date.now()}`, color: 'White', units5kg: 0, units20kg: 0, units40kg: 0 };
    setManualOrder(prev => ({ ...prev, primerItems: [...prev.primerItems, newItem] }));
  };

  const updatePrimerItem = (itemId, field, value) => {
    setManualOrder(prev => ({
      ...prev,
      primerItems: prev.primerItems.map(item =>
        item.id === itemId ? { ...item, [field]: typeof value === 'number' ? Math.max(0, value) : value } : item
      )
    }));
  };

  const removePrimerItem = (itemId) => {
    setManualOrder(prev => ({ ...prev, primerItems: prev.primerItems.filter(item => item.id !== itemId) }));
  };

  const addHighBuildItem = () => {
    const newItem = { id: `hb_${Date.now()}`, color: HIGH_BUILD_EPOXY.colors[0]?.code || null, units5kg: 0, units25kg: 0 };
    setManualOrder(prev => ({ ...prev, highBuildItems: [...prev.highBuildItems, newItem] }));
  };

  const updateHighBuildItem = (itemId, field, value) => {
    setManualOrder(prev => ({
      ...prev,
      highBuildItems: prev.highBuildItems.map(item =>
        item.id === itemId ? { ...item, [field]: typeof value === 'number' ? Math.max(0, value) : value } : item
      )
    }));
  };

  const removeHighBuildItem = (itemId) => {
    setManualOrder(prev => ({ ...prev, highBuildItems: prev.highBuildItems.filter(item => item.id !== itemId) }));
  };

  const addFlakeItem = (packSize = '1kg') => {
    const limited = packSize !== '20kg';
    const firstColor = (limited ? FLAKE_PRODUCTS.limitedColors : FLAKE_PRODUCTS.allColors)[0]?.name || null;
    const newItem = { id: `flake_${Date.now()}`, packSize, color: firstColor, quantity: 1 };
    setManualOrder(prev => ({ ...prev, flakeItems: [...prev.flakeItems, newItem] }));
  };

  const updateFlakeItem = (itemId, field, value) => {
    setManualOrder(prev => ({
      ...prev,
      flakeItems: prev.flakeItems.map(item =>
        item.id === itemId ? { ...item, [field]: typeof value === 'number' ? Math.max(0, value) : value } : item
      )
    }));
  };

  const removeFlakeItem = (itemId) => {
    setManualOrder(prev => ({ ...prev, flakeItems: prev.flakeItems.filter(item => item.id !== itemId) }));
  };

  const updateManualOrder = (field, value) => {
    setManualOrder(prev => ({ ...prev, [field]: Math.max(0, value) }));
  };

  // High build steps
  const highBuildSteps = [
    { num: 1, title: 'Define Areas' },
    { num: 2, title: 'Floor Prep' },
    { num: 3, title: 'System' },
    { num: 4, title: 'Products' },
    { num: 5, title: 'Summary' }
  ];

  // High Build Area Functions
  const addHighBuildArea = () => {
    setHighBuildAreas(prev => [...prev, {
      id: `hb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `Area ${prev.length + 1}`,
      m2: '',
      needsSmoothingCompounds: null,
      smoothingCompounds: {},
      smoothingDepth: 3,
      needsSubFloorPrimer: null,
      subFloorPrimers: {},
      systemType: null,
      primerColor: 'Grey',
      highBuildColor: null,
      highBuildUnits: 0,
      primerUnits: 0,
      flakeColor: null,
      flakeKg: 0,
      polyasparticUnits: 0
    }]);
  };

  const removeHighBuildArea = (areaId) => {
    setHighBuildAreas(prev => prev.filter(a => a.id !== areaId));
  };

  const updateHighBuildArea = (areaId, field, value) => {
    setHighBuildAreas(prev => prev.map(area =>
      area.id === areaId ? { ...area, [field]: value } : area
    ));
  };

  const calculateHighBuildProducts = (area) => {
    const m2 = parseInt(area.m2) || 0;
    if (m2 === 0 || !area.systemType) return null;
    const system = HIGH_BUILD_SYSTEMS[area.systemType];
    const unitsPerCoat = Math.ceil(m2 / 20);
    const products = {
      primerUnits: system.primerCoats * unitsPerCoat,
      highBuildUnits: system.highBuildCoats * unitsPerCoat,
      flakeKg: 0,
      polyasparticUnits: 0
    };
    if (system.hasFlakes) {
      products.flakeKg = Math.ceil(m2 * system.flakeRate);
      products.polyasparticUnits = Math.ceil(m2 / 20);
    }
    return products;
  };

  const initializeHighBuildProducts = (areaId) => {
    setHighBuildAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const products = calculateHighBuildProducts(area);
      if (!products) return area;
      const flakeKg = products.flakeKg || 0;
      let flake20kg = 0, flake10kg = 0, flake1kg = 0;
      if (flakeKg > 0) {
        flake20kg = Math.floor(flakeKg / 20);
        const remaining = flakeKg % 20;
        flake10kg = Math.floor(remaining / 10);
        flake1kg = remaining % 10;
      }
      return {
        ...area,
        primerUnits: products.primerUnits,
        highBuildUnits: products.highBuildUnits,
        flake1kg: area.flake1kg ?? flake1kg,
        flake10kg: area.flake10kg ?? flake10kg,
        flake20kg: area.flake20kg ?? flake20kg,
        polyasparticUnits: products.polyasparticUnits
      };
    }));
  };

  const calculateHighBuildTotal = () => {
    let total = 0;
    highBuildAreas.forEach(area => {
      if (area.needsSmoothingCompounds) {
        FLOOR_PREP_PRODUCTS.smoothing_compounds.forEach(product => {
          const qty = area.smoothingCompounds?.[product.id] || 0;
          total += qty * product.price;
        });
      }
      if (area.needsSubFloorPrimer) {
        FLOOR_PREP_PRODUCTS.primers.forEach(product => {
          const qty = area.subFloorPrimers?.[product.id] || 0;
          total += qty * product.price;
        });
      }
      const highBuildPrice = HIGH_BUILD_EPOXY.packs[0].price;
      total += (area.primerUnits || 0) * highBuildPrice;
      total += (area.highBuildUnits || 0) * highBuildPrice;
      const flake1kg = area.flake1kg || 0;
      const flake10kg = area.flake10kg || 0;
      const flake20kg = area.flake20kg || 0;
      total += flake1kg * FLAKE_PRODUCTS.packs[0].price;
      total += flake10kg * FLAKE_PRODUCTS.packs[1].price;
      total += flake20kg * FLAKE_PRODUCTS.packs[2].price;
      if (area.polyasparticUnits > 0) {
        const m2 = parseInt(area.m2) || 0;
        const optimalPacks = calculateOptimalPacks(m2, POLYASPARTIC_SEAL.packs);
        optimalPacks.forEach(pack => { total += pack.price * pack.quantity; });
      }
    });
    return total;
  };

  const getHighBuildItemCount = () => {
    let count = 0;
    highBuildAreas.forEach(area => {
      if (area.needsSmoothingCompounds) { Object.values(area.smoothingCompounds || {}).forEach(qty => count += qty); }
      if (area.needsSubFloorPrimer) { Object.values(area.subFloorPrimers || {}).forEach(qty => count += qty); }
      count += (area.primerUnits || 0);
      count += (area.highBuildUnits || 0);
      count += (area.flake1kg || 0);
      count += (area.flake10kg || 0);
      count += (area.flake20kg || 0);
      if (area.polyasparticUnits > 0) count += 1;
    });
    return count;
  };

  // Manual Order calculations
  const calculateManualOrderTotal = () => {
    let total = 0;
    if (manualOrder.needsSmoothingCompounds) {
      FLOOR_PREP_PRODUCTS.smoothing_compounds.forEach(product => {
        const qty = manualOrder.smoothingCompounds?.[product.id] || 0;
        total += qty * product.price;
      });
    }
    if (manualOrder.needsSubFloorPrimer) {
      FLOOR_PREP_PRODUCTS.primers.forEach(product => {
        const qty = manualOrder.subFloorPrimers?.[product.id] || 0;
        total += qty * product.price;
      });
    }
    (manualOrder.primerItems || []).forEach(item => {
      total += (item.units5kg || 0) * EPOXY_PRIMERS['2_part'].packs[0].price;
      total += (item.units20kg || 0) * EPOXY_PRIMERS['2_part'].packs[1].price;
      total += (item.units40kg || 0) * EPOXY_PRIMERS['2_part'].packs[2].price;
    });
    (manualOrder.highBuildItems || []).forEach(item => {
      total += (item.units5kg || 0) * HIGH_BUILD_EPOXY.packs[0].price;
      total += (item.units25kg || 0) * HIGH_BUILD_EPOXY.packs[1].price;
    });
    (manualOrder.flakeItems || []).forEach(item => {
      const packIndex = item.packSize === '1kg' ? 0 : item.packSize === '10kg' ? 1 : 2;
      total += (item.quantity || 0) * FLAKE_PRODUCTS.packs[packIndex].price;
    });
    total += (manualOrder.polyaspartic5m2 || 0) * POLYASPARTIC_SEAL.packs[0].price;
    total += (manualOrder.polyaspartic20m2 || 0) * POLYASPARTIC_SEAL.packs[1].price;
    return total;
  };

  const getManualOrderItemCount = () => {
    let count = 0;
    if (manualOrder.needsSmoothingCompounds) { Object.values(manualOrder.smoothingCompounds || {}).forEach(qty => count += qty); }
    if (manualOrder.needsSubFloorPrimer) { Object.values(manualOrder.subFloorPrimers || {}).forEach(qty => count += qty); }
    (manualOrder.primerItems || []).forEach(item => {
      count += (item.units5kg || 0); count += (item.units20kg || 0); count += (item.units40kg || 0);
    });
    (manualOrder.highBuildItems || []).forEach(item => {
      count += (item.units5kg || 0); count += (item.units25kg || 0);
    });
    (manualOrder.flakeItems || []).forEach(item => { count += (item.quantity || 0); });
    count += (manualOrder.polyaspartic5m2 || 0);
    count += (manualOrder.polyaspartic20m2 || 0);
    return count;
  };

  // Manual order floor prep
  const updateManualFloorPrepQty = (category, productId, delta) => {
    setManualOrder(prev => ({
      ...prev,
      [category]: { ...prev[category], [productId]: Math.max(0, (prev[category]?.[productId] || 0) + delta) }
    }));
  };

  return {
    // State
    highBuildAreas, manualOrder,
    showInstallationGuide, setShowInstallationGuide,
    showColourChart, setShowColourChart,
    highBuildSteps,
    // High Build area functions
    addHighBuildArea, removeHighBuildArea, updateHighBuildArea,
    calculateHighBuildProducts, initializeHighBuildProducts,
    calculateHighBuildTotal, getHighBuildItemCount,
    // Manual order functions
    addPrimerItem, updatePrimerItem, removePrimerItem,
    addHighBuildItem, updateHighBuildItem, removeHighBuildItem,
    addFlakeItem, updateFlakeItem, removeFlakeItem,
    updateManualOrder, setManualOrder,
    calculateManualOrderTotal, getManualOrderItemCount,
    updateManualFloorPrepQty
  };
};

export default useHighBuildOrder;
